/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { $getRoot, CommandPayloadType, LexicalCommand, LexicalEditor } from 'lexical';
import { usePropsUpdater } from 'modules/Lexical/hooks/usePropsUpdater';
import { LexicalAbbreviationValues, ABBREVIATION_CHANGE } from 'modules/Lexical/Plugins/AbbreviationPlugin';
import { $selectAbbreviation } from 'modules/Lexical/Plugins/AbbreviationPlugin/utils';
import { BASE_PROPS_COMMAND, BaseProps } from 'modules/Lexical/Plugins/BasePropsPlugin';
import { CustomListOnChangeProps, BULLET_COLOR_COMMAND } from 'modules/Lexical/Plugins/CustomListPlugin';
import { FONT_COMMAND, FontProps } from 'modules/Lexical/Plugins/FontPlugin';
import { LINK_COMMAND, LinkProps } from 'modules/Lexical/Plugins/LinkNodePlugin';
import { TEXT_BLOCK_COMMAND, TextBlockProps } from 'modules/Lexical/Plugins/TextBlockPlugin';
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { TextEditorHook, TextEditorSetters, TextEditorProps } from 'components/ArtboardAssets/Text/hooks/TextEditorHook';
import * as Constants from 'const';
import * as Models from 'models';
import { hasToken } from 'utils/autoheight';
import { $isProjectMetadataTokenNode } from '../nodes/ProjectMetadataTokenNode';
import { HEADING_TAG_COMMAND } from '../Plugins/HeadingTagPlugin/commands';
import { NoWrapProps, TEXT_NOWRAP_COMMAND } from '../Plugins/NoWrapPlugin';
import { PROJECT_METADATA_COMMAND } from '../Plugins/ProjectMetadataPlugin/commands';
import { normalizeEditorState } from './helpers';

export type LexicalOnChange = {
  abbreviations: (values: LexicalAbbreviationValues) => void;
  baseProps: (values: BaseProps) => void;
  textNoWrap: (values: NoWrapProps) => void;
  textBlock: (values: TextBlockProps) => void;
  bulletToggle: (values: CustomListOnChangeProps) => void;
  font: (values: FontProps) => void;
  link: (values: LinkProps) => void;
  textContent: (value: string) => void;
  headingTag: (value: Constants.HeadingTagType) => void;
};

type LexicalTextEditorHook = TextEditorHook & {
  ref: RefObject<LexicalEditor>;
  onChange: LexicalOnChange;
  brandStyleChangedFromEditor: boolean;
  onBrandStyleChangedFromEditor: (value: boolean) => void;
};

export function useLexicalTextEditor(): LexicalTextEditorHook {

  const ref = useRef<LexicalEditor>(null);

  const [brandStyleChangedFromEditor, setBrandStyleChangedFromEditor] = useState(false);

  const onBrandStyleChangedFromEditor = useCallback((value: boolean) => {
    setBrandStyleChangedFromEditor(value);
  }, []);

  const { props, updateProps } = usePropsUpdater<TextEditorProps>({
    abbreviationId: undefined,
    abbreviationTerm: undefined,
    blockLineHeight: undefined,
    blockType: undefined,
    bulletColor: undefined,
    fontColor: undefined,
    fontFamily: undefined,
    fontSize: undefined,
    charStyle: [],
    link: undefined,
    linkApplicable: false,
    scriptStyle: undefined,
    textNoWrap: false,
    headingTag: undefined,
  });

  const [textContent, setTextContent] = useState('');

  const dispatchEditorCommand = useCallback(<T extends LexicalCommand<unknown>>(
    type: T,
    payload: CommandPayloadType<T>,
  ): void => {
    if (!ref.current) {
      return;
    }
    ref.current.dispatchCommand(type, payload);
  }, []);

  const setters = useMemo((): TextEditorSetters => ({
    abbreviationId: (id: string | undefined) => dispatchEditorCommand(ABBREVIATION_CHANGE, id),
    projectMetadataToken: value => dispatchEditorCommand(PROJECT_METADATA_COMMAND.INSERT_TOKEN, value),
    blockLineHeight: value => dispatchEditorCommand(TEXT_BLOCK_COMMAND.BLOCK_LINE_HEIGHT, value),
    blockType: type => dispatchEditorCommand(TEXT_BLOCK_COMMAND.BLOCK_TYPE, type),
    bulletColor: color => dispatchEditorCommand(BULLET_COLOR_COMMAND, color),
    fontColor: brandColor => dispatchEditorCommand(FONT_COMMAND.BRAND_COLOR, brandColor),
    fontFamily: (
      brandFont: Models.BrandFontMap,
      characterStyle: Models.CharacterStyleMap,
    ) => dispatchEditorCommand(FONT_COMMAND.BRAND_FONT, { brandFont, characterStyle }),
    fontSize: value => dispatchEditorCommand(FONT_COMMAND.SIZE, value),
    charStyle: style => dispatchEditorCommand(FONT_COMMAND.CHAR_STYLE, style),
    link: (value: string | undefined) => dispatchEditorCommand(LINK_COMMAND, value),
    scriptStyle: value => dispatchEditorCommand(BASE_PROPS_COMMAND.SCRIPT_STYLE, value),
    textNoWrap: () => dispatchEditorCommand(TEXT_NOWRAP_COMMAND, undefined),
    headingTag: value => dispatchEditorCommand(HEADING_TAG_COMMAND, value),
  }), [dispatchEditorCommand]);

  const onChange: LexicalOnChange = useMemo(() => ({
    abbreviations: values => updateProps({
      abbreviationId: values.abbreviationId,
      abbreviationTerm: values.abbreviationTerm,
    }),
    baseProps: (values: BaseProps) => updateProps({
      scriptStyle: values.scriptStyle,
    }),
    textNoWrap: (values: NoWrapProps) => updateProps({
      textNoWrap: values.textNoWrap,
    }),
    textBlock: (values: TextBlockProps) => updateProps(prev => ({
      blockLineHeight: values.blockLineHeight,
      blockType: values.blockType ?? (prev.blockType ?? Constants.TextHorizontalAlignmentType.LEFT),
    })),
    bulletToggle: values => updateProps(prev => ({
      blockType: values
        ? Constants.TextHorizontalAlignmentType.UNORDERED_LIST
        : (prev.blockType ?? Constants.TextHorizontalAlignmentType.LEFT),
      bulletColor: values?.color,
    })),
    font: values => updateProps({
      fontColor: values.brandColor,
      fontFamily: values.brandFont,
      fontSize: values.size,
      charStyle: values.charStyle,
    }),
    link: values => updateProps({
      link: values.link,
      linkApplicable: values.linkApplicable,
    }),
    textContent: value => setTextContent(value),
    headingTag: value => updateProps({ headingTag: value }),
  }), [updateProps]);

  const applyBrandStyleValues = useCallback(() => { }, []); // ???

  const returnFocusToEditor = useCallback(() => {
    // https://github.com/facebook/lexical/issues/4460
    ref.current?._rootElement?.focus();
  }, []);

  // CELL ACTION EXECUTORS

  const setCursorOnAbbreviation = (abbreviationId: string, abbreviationNumber: number | undefined): boolean => {
    let results = false;
    ref.current?.update(() => {
      results = $selectAbbreviation(abbreviationId, abbreviationNumber);
    }, { discrete: true });

    return results;
  };

  const saveStateToTextComponent = useCallback((document: Models.TextComponentMap): Models.TextComponentMap => {
    const editorState = ref.current?.getEditorState();

    let lexicalState: string | null = null;
    let name = '';
    if (editorState) {
      lexicalState = JSON.stringify(editorState);
      name = editorState.read(() => $getRoot().getTextContent());
    }

    return document.withMutations(doc => doc
      .set('lexicalState', lexicalState)
      .set('hasProjectMetadataToken', editorState
        ?.read(() => $getRoot().getAllTextNodes().filter($isProjectMetadataTokenNode).length > 0))
      .set('name', name),
    );
  }, []);

  const hasChanges = useCallback((document: Models.TextComponentMap | undefined): boolean => {
    if (!document) {
      return true;
    }
    const current = document.get('lexicalState') || null;
    const editorState = ref.current?.getEditorState();
    const next = editorState ? JSON.stringify(editorState) : null;

    if (!current || !next) {
      return !!current !== !!next;
    }

    // Normalize states by removing auto-added fields
    const normalizedCurrent = normalizeEditorState(current);
    const normalizedNext = normalizeEditorState(next);

    return normalizedCurrent !== normalizedNext;
  }, []);

  return {
    // TextEditorHook --------------
    hasChanges,
    hasTextContent: Boolean(textContent.length),
    hasCustomToken: hasToken(textContent),
    props,
    setters,
    applyBrandStyleValues,
    returnFocusToEditor,
    saveStateToTextComponent,
    // TextEditorHook - CELL ACTION EXECUTORS
    setCursorOnAbbreviation,
    // custom --------------
    ref,
    onChange,
    brandStyleChangedFromEditor,
    onBrandStyleChangedFromEditor,
  };
}
